"use client";

import Image from "next/image";
import Link from "next/link";
import { Sheet, SheetContent, SheetTitle } from "../../shadcn/ui/sheet";
import { DashboardEvents, trackEvent } from "@punchup/analytics";
import {
  PunchupDashboardSidebar,
  type PunchupDashboardSidebarDrilldown,
  type PunchupDashboardSidebarItem,
  type PunchupDashboardSidebarProps,
} from "@punchup/ui-common/punchup/composites/navigation/dashboard-sidebar/PunchupDashboardSidebar";
import { type DashboardSidebarMenuItem } from "./types";
import logoWithIcon from "../../resources/punchupLogoIcon.svg";

interface SlideOutDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItems: DashboardSidebarMenuItem[];
  /**
   * The rows pinned below the destinations. Build them with
   * `dashboardFooterItems()`, the same list the desktop rail renders, so both navs
   * carry the same rows.
   */
  footerItems?: PunchupDashboardSidebarItem[];
  /** The settings variant, when one should show: on a settings page (the desktop rail shows the same), or when the user opens it in place. */
  drilldown?: PunchupDashboardSidebarDrilldown;
  /** Called with every row click, after the click is tracked. */
  onItemClick?: PunchupDashboardSidebarProps["onItemClick"];
}

export function SlideOutDrawer({
  open,
  onOpenChange,
  menuItems,
  footerItems,
  drilldown,
  onItemClick,
}: SlideOutDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        // The rail brings its own width and padding, so the sheet contributes neither.
        className="flex w-[var(--punchup-dashboard-nav-width)] flex-col gap-0 bg-ivory p-0 !z-[1000] [&>button.absolute]:hidden data-[state=open]:duration-200 data-[state=closed]:duration-150"
      >
        <SheetTitle className="sr-only">Dashboard Navigation</SheetTitle>
        <PunchupDashboardSidebar
          // The logo, not the account card: on mobile the account switcher
          // already sits in the header bar that opens this drawer, and showing
          // it twice would ask the same question in two places.
          header={
            <Image
              src={logoWithIcon}
              alt="Punchup logo"
              width={141}
              height={24}
              className="h-[24px] w-auto self-start ml-[var(--punchup-nav-item-padding-x)]"
            />
          }
          items={menuItems}
          footerItems={footerItems}
          drilldown={drilldown}
          linkComponent={Link}
          onItemClick={(payload) => {
            trackEvent(
              DashboardEvents.COMEDIAN_CLICK_DASHBOARD_SIDEBAR,
              payload
            );
            onItemClick?.(payload);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}
